/**
 * 정보처리기사 합격률 데이터
 * 이 파일에서 합격률 데이터를 중앙 관리합니다.
 *
 * 사용처:
 * - src/app/past-exam/page.tsx
 * - src/app/exam-registration/contents/jeongcheogi-pass-rate.mdx
 */

/** 회차 체감 난이도 — 합격률과 별개로 표기하는 수기 편집 값 (JCG-2167). */
export type RoundDifficulty = "쉬움" | "보통" | "어려움";

export interface PassRateByRound {
  name: string;
  "실기 합격률": number;
  /** 회차 체감 난이도 (수기 편집). 채워두면 우측 사이드바 회차 카드에 배지로 노출(JCG-2167). */
  difficulty?: RoundDifficulty;
  /** 회차 한 줄 코멘트 (수기 편집). 채워두면 회차 카드에 노출(JCG-2167). */
  comment?: string;
  [key: string]: string | number | undefined;
}

export interface PassRateByYear {
  name: string;
  합격률: number;
  [key: string]: string | number;
}

/**
 * 연도별 회차별 실기 합격률 데이터
 * 차트 컴포넌트에서 사용하는 형식
 */
export const practicalPassRateByRound: PassRateByRound[] = [
  { name: "2020년 1회", "실기 합격률": 5.3, difficulty: "어려움", comment: "첫 실기 시험이라 합격률 5.3%로 역대 가장 낮았던 회차예요." },
  { name: "2020년 2회", "실기 합격률": 21.0, difficulty: "보통", comment: "합격률이 평균 수준이었던 회차예요." },
  { name: "2020년 3회", "실기 합격률": 18.3, difficulty: "보통", comment: "합격률이 평균 수준이었던 회차예요." },
  { name: "2020년 4회", "실기 합격률": 13.9, difficulty: "어려움", comment: "합격률이 13.9%로 낮아 어려웠던 회차예요." },
  { name: "2020년 5회", "실기 합격률": 20.9, difficulty: "보통", comment: "합격률이 평균 수준이었던 회차예요." },
  { name: "2021년 1회", "실기 합격률": 39.5, difficulty: "쉬움", comment: "역대 가장 높은 39.5% 합격률을 기록한 회차예요." },
  { name: "2021년 2회", "실기 합격률": 27.7, difficulty: "보통", comment: "합격률이 평균보다 조금 높았던 회차예요." },
  { name: "2021년 3회", "실기 합격률": 23.6, difficulty: "보통", comment: "합격률이 평균 수준이었던 회차예요." },
  { name: "2022년 1회", "실기 합격률": 25.6, difficulty: "보통", comment: "합격률이 평균 수준이었던 회차예요." },
  { name: "2022년 2회", "실기 합격률": 16.1, difficulty: "어려움", comment: "합격률이 16.1%로 낮아 어려웠던 회차예요." },
  { name: "2022년 3회", "실기 합격률": 20.4, difficulty: "보통", comment: "합격률이 평균 수준이었던 회차예요." },
  { name: "2023년 1회", "실기 합격률": 27.5, difficulty: "보통", comment: "합격률이 평균보다 조금 높았던 회차예요." },
  { name: "2023년 2회", "실기 합격률": 17.9, difficulty: "어려움", comment: "합격률이 17.9%로 낮아 어려웠던 회차예요." },
  { name: "2023년 3회", "실기 합격률": 17.7, difficulty: "어려움", comment: "합격률이 17.7%로 낮아 어려웠던 회차예요." },
  { name: "2024년 1회", "실기 합격률": 36.8, difficulty: "쉬움", comment: "합격률이 36.8%로 높아 비교적 평이했던 회차예요." },
  { name: "2024년 2회", "실기 합격률": 27.7, difficulty: "보통", comment: "합격률이 평균보다 조금 높았던 회차예요." },
  { name: "2024년 3회", "실기 합격률": 20.7, difficulty: "보통", comment: "합격률이 평균 수준이었던 회차예요." },
  { name: "2025년 1회", "실기 합격률": 15.1, difficulty: "어려움", comment: "합격률이 15.1%로 낮아 까다로웠던 회차예요." },
  { name: "2025년 2회", "실기 합격률": 27.8, difficulty: "보통", comment: "합격률이 평균보다 조금 높았던 회차예요." },
  { name: "2025년 3회", "실기 합격률": 23.9, difficulty: "보통", comment: "합격률이 평균 수준이었던 회차예요." },
  { name: "2026년 1회", "실기 합격률": 18.1, difficulty: "어려움", comment: "응시자 23,898명 중 4,335명이 합격해 합격률 18.1%를 기록한 회차예요." },
  { name: "2026년 2회", "실기 합격률": 34.7, difficulty: "쉬움", comment: "응시자 23,446명 중 8,142명이 합격해 합격률 34.7%로, 2021년 1회·2024년 1회에 이어 역대 3번째로 높았던 회차예요." },
];

/**
 * 연도별 필기 합격률 데이터
 */
export const writtenPassRateByYear: PassRateByYear[] = [
  { name: "2020년", 합격률: 57.3 },
  { name: "2021년", 합격률: 63.6 },
  { name: "2022년", 합격률: 56.1 },
  { name: "2023년", 합격률: 59.0 },
  { name: "2024년", 합격률: 60.8 },
  { name: "2025년", 합격률: 65.7 },
  { name: "2026년(2회 기준)", 합격률: 64.8 },
];

/**
 * 연도별 실기 합격률 데이터
 */
export const practicalPassRateByYear: PassRateByYear[] = [
  { name: "2020년", 합격률: 17.7 },
  { name: "2021년", 합격률: 30.8 },
  { name: "2022년", 합격률: 20.8 },
  { name: "2023년", 합격률: 21.0 },
  { name: "2024년", 합격률: 28.9 },
  { name: "2025년", 합격률: 22.1 },
  { name: "2026년(2회 기준)", 합격률: 26.4 },
];

// ───────────────────────────────────────────────────────────────────────────
// 회차 풀이 페이지 우측 사이드바 회차 통계(JCG-2167)
// ───────────────────────────────────────────────────────────────────────────

/**
 * 우측 사이드바 회차 통계 카드(RoundStatsCard)가 쓰는 한 회차의 묶음.
 * 실기 합격률이 아직 발표되지 않은 최신 회차는 `passRate: null`("집계 전").
 */
export interface RoundStats {
  /** "2026년 1회" */
  name: string;
  /** 실기 합격률(%). null = 아직 발표 전(집계 전). */
  passRate: number | null;
  difficulty?: RoundDifficulty;
  comment?: string;
}

/**
 * 실기 합격률 미발표(집계 전) 최신 회차의 난이도·코멘트.
 * 합격률이 발표되면 해당 회차를 practicalPassRateByRound 로 옮기고 여기서 제거한다.
 *
 * 합격률 발표 직후 ~ 다음 회차 리뷰 게시 사이에는 비어 있는 것이 정상이다. 그동안
 * RoundStats.passRate 의 null 분기와 RoundStatsCard 의 "집계 전" 표시는 도달하지
 * 않지만, 다음 회차에 다시 쓰므로 죽은 코드로 보고 지우지 않는다.
 */
const pendingRounds: RoundStats[] = [];

/**
 * 전체 발표 회차의 실기 합격률 평균(소수 첫째 자리). 카드의 "평균 대비" 칩 계산용.
 */
export const averagePracticalPassRate =
  Math.round(
    (practicalPassRateByRound.reduce((sum, r) => sum + r["실기 합격률"], 0) /
      practicalPassRateByRound.length) *
      10,
  ) / 10;

/**
 * 회차 풀이 페이지 slug → "YYYY년 N회" 회차명. 회차 형식이 아니면 null.
 *
 * 풀이("2026-1")·해설("2026-1-review") slug 모두 같은 회차로 매핑한다.
 */
function roundSlugToName(slug: string): string | null {
  const m = /^(\d{4})-(\d+)(?:-.*)?$/.exec(slug);
  if (!m) return null;
  return `${m[1]}년 ${m[2]}회`;
}

/**
 * 회차 풀이 페이지 slug 로 해당 회차의 합격률·난이도·코멘트를 묶어 반환(JCG-2167).
 *
 * 발표 회차는 practicalPassRateByRound, 미발표 최신 회차는 pendingRounds 에서 매칭한다.
 * 어느 쪽에도 없으면 null → 카드 미노출(모의고사 등 회차 개념이 없는 페이지 포함).
 */
export function getRoundStats(slug: string): RoundStats | null {
  const name = roundSlugToName(slug);
  if (!name) return null;

  const published = practicalPassRateByRound.find((r) => r.name === name);
  if (published) {
    return {
      name,
      passRate: published["실기 합격률"],
      difficulty: published.difficulty,
      comment: published.comment,
    };
  }

  const pending = pendingRounds.find((r) => r.name === name);
  if (pending) return { ...pending };

  return null;
}

/**
 * 회차 풀이/해설 페이지 pathname → 회차 통계(JCG-2167). 회차 페이지가 아니면 null.
 *
 * `/past-exam/<YYYY>/<slug>`(3세그먼트)에서만 slug 를 뽑아 getRoundStats 로 위임한다.
 * 모의고사 등 다른 셸 페이지는 매칭되지 않아 null → 카드 미노출. slug→회차 매핑을
 * 이 파일에 모아 ArticleShell 의 인라인 경로 파싱 중복을 없앤다.
 */
export function getRoundStatsForPath(pathname: string): RoundStats | null {
  const m = /^\/past-exam\/[^/]+\/([^/]+)\/?$/.exec(pathname);
  return m ? getRoundStats(m[1]) : null;
}
